/**
 * 需給ギャップの上下 2 枚の Y 軸 (`./mainChartAxis` / `./diffChartAxis`) が共有する
 * 「きりのいい刻み」(AC-SDG-006、 1 / 2 / 5 × 10^n) の選び方と目盛り列の作り方。
 *
 * 片側レンジ (メイン) か 0 対称レンジ (差分) か・空データ時の既定幅・目盛り本数の
 * 上限は 2 枚で違うので各軸モジュールが持ち、 ここは 2 枚で必ず同じでなければ
 * ならない部分 (刻みの丸め・区間数の数え方・浮動小数の桁あふれ処理) だけを持つ。
 *
 * 入札後ポジション chart の `~/components/bidding/common/positionChartAxis.ts` は
 * 同型の「0 を含む nice-step 軸」だが再利用しない。 あちらは spot-bidding
 * AC-BSP-016 (「刻み幅の算出方法は EN 判断範囲」) の下で余白を詰めるために
 * 1.5 / 2.5 系 (15MW・250MW 等) を刻み候補に持ち、 AC-SDG-006 が定める
 * 1 / 2 / 5 × 10^n を外れるため。
 */

const NICE_FRACTIONS = [1, 2, 5] as const

/** 浮動小数の丸め誤差で ceil が 1 段跳ねるのを防ぐ許容値。 */
const EPSILON = 1e-9

/** 浮動小数の桁あふれ (0.1 × 3 = 0.30000000000000004) を落として目盛りラベルを綺麗に保つ。 */
function roundTick(value: number): number {
  return Number(value.toPrecision(12))
}

/**
 * `niceScale` が受け付ける `max` の上限。 これを超える有限値 (1.7e308 等) では
 * 軸上端 (`segments × step`) が Infinity へ桁あふれし、 recharts が domain を
 * 描けなくなる。 実データの MW は 10^4 台なのでこの上限は実運用では効かない。
 */
const MAX_SCALE_INPUT = Number.MAX_SAFE_INTEGER

/** `rough` 以上で最小の「きりのいい」刻み幅 (= 1 / 2 / 5 × 10^n)。 */
function niceStep(rough: number): number {
  if (!Number.isFinite(rough) || rough <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalized = rough / magnitude
  const fraction = NICE_FRACTIONS.find((c) => normalized <= c + EPSILON)
  const step = roundTick((fraction ?? 10) * magnitude)
  // 非正規化数 (5e-324 等) では `10 ** 指数` が 0 へ桁落ちして step が 0 になる。
  // step 0 は区間数を Infinity にし `stepTicks` のループが止まらなくなるため 1 に倒す。
  return step > 0 ? step : 1
}

export type NiceScale = {
  /** きりのいい刻み幅 (= 1 / 2 / 5 × 10^n)。 */
  step: number
  /** `max` を覆うのに実際に要る区間数 (1 以上 `maxSegments` 以下)。 */
  segments: number
}

/**
 * `max` を `maxSegments` 区間以内で覆う最小のきりのいい刻みと、 実際に要る区間数。
 * 最小の刻み = 余白が最小 = 系列が描画高を最も広く使う。
 *
 * `max` は {@link MAX_SCALE_INPUT} で頭打ちにする。 そこまで大きい値が来た時点で
 * 入力が壊れているので、 軸がデータを覆うことより「有限の domain を返して描画を
 * 止めない」ことを優先する。
 */
export function niceScale(max: number, maxSegments: number): NiceScale {
  const cappedMax = Math.min(max, MAX_SCALE_INPUT)
  const step = niceStep(cappedMax / maxSegments)
  const segments = segmentsFor(cappedMax, step)
  if (segments <= maxSegments) return { step, segments }

  // `niceStep` の許容 (EPSILON) の分だけ刻みが `max / maxSegments` を
  // 下回る帯 (max が刻みラダーの整数倍を相対 1e-9 だけ上回るとき) では、 区間数が
  // 上限を 1 つ超える (`niceScale(500.0000005, 5)` が 6 区間 = 目盛り 7 本)。
  // 1 段上の刻みに上げて上限内に収める (きりのいい刻みの 1.5 倍は必ず次の
  // きりのいい刻みの手前に落ちる: 1 → 1.5 → 2 / 2 → 3 → 5 / 5 → 7.5 → 10)。
  const widerStep = niceStep(step * 1.5)
  return { step: widerStep, segments: segmentsFor(cappedMax, widerStep) }
}

/** `max` を `step` 刻みで覆うのに要る区間数 (1 以上)。 */
function segmentsFor(max: number, step: number): number {
  return Math.max(1, Math.ceil(max / step - EPSILON))
}

/** `step` 刻みの目盛り列 (index `from` から `to` まで)。 */
export function stepTicks(step: number, from: number, to: number): number[] {
  const ticks: number[] = []
  for (let i = from; i <= to; i += 1) {
    ticks.push(roundTick(i * step))
  }
  return ticks
}
